import React, { useState, useEffect, useRef } from 'react';
import { Sparkles } from 'lucide-react';

export interface Contact {
    id: string;
    name: string;
    email: string;
    role: string;
    aiEnabled: boolean;
}

interface ContactAutocompleteProps {
    contacts: Contact[];
    value: string;
    onChange: (value: string) => void;
    onSelectContact?: (contact: Contact) => void;
    placeholder?: string;
    className?: string;
}

const ContactAutocomplete: React.FC<ContactAutocompleteProps> = ({
    contacts,
    value,
    onChange,
    onSelectContact,
    placeholder = "Type to search contacts...",
    className = "",
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Filter contacts based on search value
    const filteredContacts = contacts.filter(contact =>
        contact.name.toLowerCase().includes(value.toLowerCase()) ||
        contact.email.toLowerCase().includes(value.toLowerCase()) ||
        contact.role.toLowerCase().includes(value.toLowerCase())
    );

    // Reset selected index when filtered contacts change
    useEffect(() => {
        setSelectedIndex(0);
    }, [value]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                inputRef.current &&
                !inputRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen && filteredContacts.length > 0 && e.key !== 'Escape') {
            setIsOpen(true);
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex((prev) =>
                    prev < filteredContacts.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
                break;
            case 'Enter':
                e.preventDefault();
                if (isOpen && filteredContacts.length > 0) {
                    handleSelectContact(filteredContacts[selectedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                break;
            case 'Tab':
                if (isOpen && filteredContacts.length > 0) {
                    e.preventDefault();
                    handleSelectContact(filteredContacts[selectedIndex]);
                }
                break;
        }
    };

    const handleSelectContact = (contact: Contact) => {
        onChange(contact.email);
        setIsOpen(false);
        onSelectContact?.(contact);
    };

    const getInitials = (name: string) => {
        const parts = name.split(' ');
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        }
        return name.slice(0, 2).toUpperCase();
    };

    return (
        <div className={`relative ${className}`}>
            <input
                ref={inputRef}
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onFocus={() => setIsOpen(true)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />

            {isOpen && filteredContacts.length > 0 && (
                <div
                    ref={dropdownRef}
                    className="absolute z-50 w-full mt-2 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-80 overflow-y-auto"
                >
                    {filteredContacts.map((contact, index) => (
                        <button
                            key={contact.id}
                            type="button"
                            onClick={() => handleSelectContact(contact)}
                            className={`w-full px-4 py-3 text-left transition-colors flex items-center gap-3 ${index === selectedIndex
                                    ? 'bg-slate-700'
                                    : 'hover:bg-slate-800'
                                }`}
                        >
                            {/* Avatar */}
                            <div
                                className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${contact.aiEnabled
                                        ? 'bg-slate-700 text-blue-400'
                                        : 'bg-gray-700 text-gray-300'
                                    }`}
                            >
                                {getInitials(contact.name)}
                            </div>

                            {/* Contact Info */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold text-white truncate">
                                        {contact.name}
                                    </span>
                                    {contact.aiEnabled && (
                                        <Sparkles
                                            className="w-4 h-4 text-blue-400 flex-shrink-0"
                                        />
                                    )}
                                </div>
                                <div className="text-sm text-gray-400 truncate">
                                    {contact.email}
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                    {contact.role}
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {isOpen && filteredContacts.length === 0 && value.length > 0 && (
                <div
                    ref={dropdownRef}
                    className="absolute z-50 w-full mt-2 bg-slate-800 border border-slate-600 rounded-lg shadow-xl p-4 text-center text-gray-400"
                >
                    No contacts found
                </div>
            )}
        </div>
    );
};

export default ContactAutocomplete;
